"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.raiseJevBudgetSchema = exports.setJevBudgetSchema = exports.JEV_STOP_WORDS = exports.JEV_WAITING_FOR_BUDGET_NOTE = exports.JEV_WAITING_FOR_BUDGET_TITLE = exports.JEV_BUDGET_STOPS = exports.JEV_READ_BATCH_SIZE = exports.JEV_BUDGET_ASK_FROM_PERCENT = exports.JEV_BUDGET_EVENTS = exports.jevBudgetMicroUsdSchema = exports.ICP_INTERVIEW_COST_RANGE_LABEL = exports.JEV_BUDGET_TYPICAL_JEV_LINES = exports.JEV_BUDGET_WARN_AT_PERCENT = exports.JEV_BUDGET_SET_PRESETS = exports.JEV_BUDGET_RAISE_PRESETS = exports.JEV_BUDGET_APPROVAL_PRESETS = exports.JEV_BUDGET_REQUEST_PRESETS = exports.JEV_BUDGET_SUGGESTED_MICRO_USD = exports.JEV_BUDGET_MAX_MICRO_USD = exports.JEV_BUDGET_MIN_MICRO_USD = exports.MICRO_USD_PER_DOLLAR = void 0;
exports.jevBudgetLabel = jevBudgetLabel;
exports.jevBudgetAmountProblem = jevBudgetAmountProblem;
exports.jevBudgetRequestLineOf = jevBudgetRequestLineOf;
exports.jevBudgetPresetLabel = jevBudgetPresetLabel;
exports.jevReadingMicroUsdOf = jevReadingMicroUsdOf;
exports.jevBudgetCoversCvs = jevBudgetCoversCvs;
exports.jevCentsPer100Cvs = jevCentsPer100Cvs;
exports.jevBudgetHint = jevBudgetHint;
exports.jevBudgetSettledOf = jevBudgetSettledOf;
exports.jevBudgetEditOf = jevBudgetEditOf;
exports.jevBudgetStandingOf = jevBudgetStandingOf;
exports.jevBudgetRaiseOptions = jevBudgetRaiseOptions;
exports.jevBudgetWarnedNotice = jevBudgetWarnedNotice;
exports.jevBudgetUsedUpNotice = jevBudgetUsedUpNotice;
exports.jevBudgetAskedNotice = jevBudgetAskedNotice;
exports.jevBudgetChangedSentence = jevBudgetChangedSentence;
exports.jevBudgetCheckOf = jevBudgetCheckOf;
exports.jevBatchSizeOf = jevBatchSizeOf;
exports.jevBudgetEstimateWords = jevBudgetEstimateWords;
exports.jevBudgetGateOf = jevBudgetGateOf;
const zod_1 = require("zod");
const icp_1 = require("./icp");
const jev_1 = require("./jev");
// ADR 0070 — a Jev budget on every hire request. The manager says how much Jev (the ideal-candidate interview and the CV
// reading) a job may need, an approver approves or changes the amount, and the approved amount becomes the job's budget.
// Stage 1 stores and shows it; reading is not yet stopped by it (stage 2), and nobody is told yet (stage 3).
//
// Money is in millionths of a US dollar, whole numbers: the unit Jev's spend and the daily automatic-reading limit already
// use (`spentMicroUsd`, `dailyMicroUsd`), so the three can be added and compared without converting. Never a float.
exports.MICRO_USD_PER_DOLLAR = 1_000_000;
const MICRO_USD_PER_CENT = 10_000;
const TOKENS_PER_THOUSAND = 1_000;
/** The smallest and largest budget a job may have: $1 and $1,000. */
exports.JEV_BUDGET_MIN_MICRO_USD = exports.MICRO_USD_PER_DOLLAR;
exports.JEV_BUDGET_MAX_MICRO_USD = 1_000 * exports.MICRO_USD_PER_DOLLAR;
/** The amount a new request starts at, so it can be sent without touching the field. */
exports.JEV_BUDGET_SUGGESTED_MICRO_USD = 5 * exports.MICRO_USD_PER_DOLLAR;
const dollars = (...amounts) => amounts.map((amount) => amount * exports.MICRO_USD_PER_DOLLAR);
/** The manager's presets on REQ-01 and HM-03. */
exports.JEV_BUDGET_REQUEST_PRESETS = dollars(1, 5, 10, 25);
/** The approver's presets when changing the amount. */
exports.JEV_BUDGET_APPROVAL_PRESETS = dollars(1, 3, 5, 10, 25);
/** The HR Lead's raise and the first-set amounts on the job's panel. */
exports.JEV_BUDGET_RAISE_PRESETS = dollars(5, 10, 25, 50, 100);
exports.JEV_BUDGET_SET_PRESETS = dollars(1, 5, 10, 25);
/** The panel turns to its warning tone from here (and stage 3's first notification goes out). */
exports.JEV_BUDGET_WARN_AT_PERCENT = 80;
/** The hint assumes a typical profile: this many lines Jev reads. */
exports.JEV_BUDGET_TYPICAL_JEV_LINES = 6;
/** What building the ideal candidate with the assistant costs, as the Ideal candidate section already words it. */
exports.ICP_INTERVIEW_COST_RANGE_LABEL = '5–10¢';
/** The CVs the hint counts in: "per 100 CVs". */
const HINT_CVS = 100;
exports.jevBudgetMicroUsdSchema = zod_1.z
    .number({ message: 'Enter an amount in dollars.' })
    .int('Enter a whole number of cents or more.')
    .min(exports.JEV_BUDGET_MIN_MICRO_USD, 'The Jev budget is at least $1.')
    .max(exports.JEV_BUDGET_MAX_MICRO_USD, 'The Jev budget is at most $1,000.');
/** "$5.00" for dollars and up, with thousands separators ("$1,000.00"); "42¢" below a dollar. */
function jevBudgetLabel(microUsd) {
    if (microUsd >= exports.MICRO_USD_PER_DOLLAR)
        return `$${(microUsd / exports.MICRO_USD_PER_DOLLAR).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `${Math.round(microUsd / MICRO_USD_PER_CENT)}¢`;
}
/** What is wrong with a typed budget, in the words under the field; null when it is fine. Nothing typed (null) is not a problem here: the field is optional. */
function jevBudgetAmountProblem(microUsd) {
    if (microUsd === null)
        return null;
    return Number.isInteger(microUsd) && microUsd >= exports.JEV_BUDGET_MIN_MICRO_USD && microUsd <= exports.JEV_BUDGET_MAX_MICRO_USD ? null : 'Enter $1 to $1,000.';
}
/** The requester's and the approvers' one line: "$5.00 asked", "$3.00 approved (asked $5.00)", "$5.00 approved"; null when nobody asked for any. */
function jevBudgetRequestLineOf(asked, approved) {
    if (approved === null)
        return asked === null ? null : `${jevBudgetLabel(asked)} asked`;
    if (asked === null || asked === approved)
        return `${jevBudgetLabel(approved)} approved`;
    return `${jevBudgetLabel(approved)} approved (asked ${jevBudgetLabel(asked)})`;
}
/** "$5" for a whole number of dollars (a preset's face), else as `jevBudgetLabel`. */
function jevBudgetPresetLabel(microUsd) {
    return microUsd % exports.MICRO_USD_PER_DOLLAR === 0 ? `$${microUsd / exports.MICRO_USD_PER_DOLLAR}` : jevBudgetLabel(microUsd);
}
/** Jev's cost to read `cvs` CVs against a profile of `lines` lines, by the same token estimate the Ideal candidate card uses. */
function jevReadingMicroUsdOf(cvs, lines = exports.JEV_BUDGET_TYPICAL_JEV_LINES) {
    const tokens = cvs * (icp_1.JEV_TOKENS_PER_CV_ESTIMATE + lines * icp_1.JEV_TOKENS_PER_PART_ESTIMATE);
    return Math.round((tokens * jev_1.JEV_MICRO_USD_PER_THOUSAND_TOKENS) / TOKENS_PER_THOUSAND);
}
/** How many CVs a budget reads for a typical profile, to the nearest hundred below; 0 when it would not cover one. */
function jevBudgetCoversCvs(microUsd) {
    const per100 = jevReadingMicroUsdOf(HINT_CVS);
    return Math.max(0, Math.floor(microUsd / per100)) * HINT_CVS;
}
/** The cost of 100 CVs in whole cents, for "about N¢ per 100 CVs". */
function jevCentsPer100Cvs() {
    return Math.max(1, Math.round(jevReadingMicroUsdOf(HINT_CVS) / MICRO_USD_PER_CENT));
}
/** The live hint under the budget field on the request and approval screens. */
function jevBudgetHint(microUsd) {
    const cvs = jevBudgetCoversCvs(microUsd).toLocaleString('en-US');
    return `About ${jevCentsPer100Cvs()}¢ per ${HINT_CVS} CVs read, plus ${exports.ICP_INTERVIEW_COST_RANGE_LABEL} to build the ideal candidate. ${jevBudgetLabel(microUsd)} covers roughly ${cvs} CVs.`;
}
/** The amount a request settles on: what the approvers set, else what was asked for; null (no limit) when nobody asked for any. */
function jevBudgetSettledOf(request) {
    return request.jevBudgetApprovedMicroUsd ?? request.jevBudgetRequestedMicroUsd;
}
/** What editing a draft does to the budget: a new ask replaces the old one and starts the approvers' amount over; no ask in the edit leaves both as they are. */
function jevBudgetEditOf(asked) {
    return asked === undefined ? {} : { jevBudgetRequestedMicroUsd: asked, jevBudgetApprovedMicroUsd: null };
}
/** Worked out, never stored (#10): what is left of a budget and how full it is. `percentUsed` stops at 100 however far spend ran over. */
function jevBudgetStandingOf(budgetMicroUsd, spentMicroUsd) {
    const percentUsed = Math.min(100, Math.floor((spentMicroUsd / budgetMicroUsd) * 100));
    const tone = percentUsed >= 100 ? 'danger' : percentUsed >= exports.JEV_BUDGET_WARN_AT_PERCENT ? 'warning' : 'ok';
    return { leftMicroUsd: Math.max(0, budgetMicroUsd - spentMicroUsd), percentUsed, tone };
}
/** Raise amounts offered: only above the budget and never below what is already spent. */
function jevBudgetRaiseOptions(budgetMicroUsd, spentMicroUsd) {
    return exports.JEV_BUDGET_RAISE_PRESETS.filter((amount) => amount > budgetMicroUsd && amount >= spentMicroUsd);
}
exports.JEV_BUDGET_EVENTS = {
    SET: 'job.jev_budget_set',
    RAISED: 'job.jev_budget_raised',
    /** Stage 2: spend first passed 80% of the budget, announced once for each budget amount (stage 3's notification hangs on it). */
    WARNED: 'job.jev_budget_warned',
    /** Stage 2: spend reached the whole budget, announced once for each budget amount. */
    USED_UP: 'job.jev_budget_used_up',
    /** Stage 2: automatic reading stopped for the job's budget or the daily limit, once until it resumes. */
    READING_STOPPED: 'jev.reading_stopped_for_budget',
    /** Stage 2: reading that had stopped for a budget carries on. */
    READING_RESUMED: 'jev.reading_resumed_after_budget',
    /** Stage 3: a recruiter asked the HR Lead for more; open until the budget changes. */
    ASKED: 'job.jev_budget_asked',
};
// ---- Stage 3: the notifications and the ask (ADR 0070) ----
/** A recruiter may ask for more once the budget is this far spent: the same point the panel and the first notification turn warning. */
exports.JEV_BUDGET_ASK_FROM_PERCENT = exports.JEV_BUDGET_WARN_AT_PERCENT;
/** N-95: the job's Jev budget passed 80%. */
function jevBudgetWarnedNotice(input) {
    const left = jevBudgetLabel(Math.max(0, input.budgetMicroUsd - input.spentMicroUsd));
    return {
        title: `Jev budget ${exports.JEV_BUDGET_WARN_AT_PERCENT}% used: ${input.reference} ${input.jobTitle}`,
        body: `${input.reference} ${input.jobTitle}: ${left} left of ${jevBudgetLabel(input.budgetMicroUsd)}.`,
    };
}
/** N-96: the job's Jev budget is spent and reading has stopped. */
function jevBudgetUsedUpNotice(input) {
    return {
        title: `Jev budget used up: ${input.reference} ${input.jobTitle}`,
        body: `${input.reference} ${input.jobTitle}: reading has stopped. Ask for more, or raise the budget.`,
    };
}
/** N-97: a recruiter asked the HR Lead for more. */
function jevBudgetAskedNotice(input) {
    const who = input.askerName ?? 'A recruiter';
    return {
        title: `More Jev budget asked for: ${input.reference} ${input.jobTitle}`,
        body: `${who} asked HR for more Jev budget on ${input.reference} ${input.jobTitle}. It is ${jevBudgetLabel(input.spentMicroUsd)} spent of ${jevBudgetLabel(input.budgetMicroUsd)}.`,
    };
}
/** The sentence added to the request-approved message when the approver changed the amount (N-04), or null when it went through as asked. */
function jevBudgetChangedSentence(input) {
    const { requestedMicroUsd, approvedMicroUsd } = input;
    if (requestedMicroUsd === null || approvedMicroUsd === null || requestedMicroUsd === approvedMicroUsd)
        return null;
    return `The Jev budget was changed from ${jevBudgetLabel(requestedMicroUsd)} to ${jevBudgetLabel(approvedMicroUsd)} by ${input.approverName ?? 'the approver'}.`;
}
// ---- Stage 2: the limit (ADR 0070) ----
/** Automatic reading goes in batches of this many CVs, and the budget is checked before each one. */
exports.JEV_READ_BATCH_SIZE = 50;
/** What stopped an action, in the order the checks are made: the job's own budget first, then the overall limits, then the day's automatic limit. */
exports.JEV_BUDGET_STOPS = ['job_budget', 'overall_limit', 'daily_limit'];
/** The one place the order of checks lives (ADR 0070 §Stage 2); the automatic reader and every manual action call it. */
function jevBudgetCheckOf(input) {
    const jobLeft = input.budgetMicroUsd === null ? null : Math.max(0, input.budgetMicroUsd - input.spentMicroUsd);
    const stop = (stoppedBy) => ({ isAllowed: false, stoppedBy, jobLeftMicroUsd: jobLeft });
    if (jobLeft !== null && (jobLeft <= 0 || input.estimateMicroUsd > jobLeft))
        return stop('job_budget');
    const overall = input.overallLeftMicroUsd ?? null;
    if (overall !== null && (overall <= 0 || input.estimateMicroUsd > overall))
        return stop('overall_limit');
    const daily = input.dailyLeftMicroUsd ?? null;
    if (input.isAutomatic && daily !== null && daily <= 0)
        return stop('daily_limit');
    return { isAllowed: true, stoppedBy: null, jobLeftMicroUsd: jobLeft };
}
/** How many CVs the next automatic batch reads: up to a batch, and no more than the job's budget still pays for (at least one, so a read always moves). */
function jevBatchSizeOf(input) {
    const wanted = Math.min(input.waiting, exports.JEV_READ_BATCH_SIZE);
    if (input.jobLeftMicroUsd === null || input.perCvMicroUsd <= 0)
        return wanted;
    return Math.min(wanted, Math.max(1, Math.floor(input.jobLeftMicroUsd / input.perCvMicroUsd)));
}
/** "about 8¢", "under 1¢", "about $1.10": a cost in the words the buttons already use. */
function jevBudgetEstimateWords(microUsd) {
    const cents = microUsd / MICRO_USD_PER_CENT;
    if (cents < 1)
        return 'under 1¢';
    if (cents < 100)
        return `about ${Math.round(cents)}¢`;
    return `about $${(cents / 100).toFixed(2)}`;
}
/** What a button that spends Jev money says about the job's budget, and whether it is off (the server refuses too; this is only the words). */
function jevBudgetGateOf(input) {
    if (input.budgetMicroUsd === null)
        return { hasBudget: false, isBlocked: false, leftMicroUsd: null, leftWords: null, blockedWords: null, nextStep: null };
    const check = jevBudgetCheckOf({ budgetMicroUsd: input.budgetMicroUsd, spentMicroUsd: input.spentMicroUsd, estimateMicroUsd: input.estimateMicroUsd, isAutomatic: false });
    const left = check.jobLeftMicroUsd ?? 0;
    const leftWords = jevBudgetLabel(left);
    if (check.isAllowed)
        return { hasBudget: true, isBlocked: false, leftMicroUsd: left, leftWords: `${leftWords} left`, blockedWords: null, nextStep: null };
    return {
        hasBudget: true,
        isBlocked: true,
        leftMicroUsd: left,
        leftWords: `${leftWords} left`,
        // An action with no fixed price (estimate 0) is refused only when nothing is left, so it says that once (not "nothing left, 0¢ left").
        blockedWords: input.estimateMicroUsd > 0 ? `needs ${jevBudgetEstimateWords(input.estimateMicroUsd)}, ${leftWords} left` : 'nothing left',
        nextStep: input.canRaise ? 'Raise the budget in the Jev budget panel.' : 'Only HR can raise this budget. Ask them to raise it.',
    };
}
/** The Waiting-for-budget note on Ranking, when a job's budget is used up. */
exports.JEV_WAITING_FOR_BUDGET_TITLE = 'Waiting for budget.';
exports.JEV_WAITING_FOR_BUDGET_NOTE = 'Reading has stopped for this job. Candidates already read keep their results.';
/** Why an automatic read stopped, in the words the Ideal candidate card shows. */
exports.JEV_STOP_WORDS = {
    job_budget: 'Waiting for budget. This job’s Jev budget is used up. Reading carries on when HR raises it.',
    overall_limit: 'Waiting for budget. The overall Jev limit is used up. Reading carries on when HR raises it.',
    daily_limit: 'Today’s automatic reading limit is used up. New CVs are read again after midnight (Dhaka time).',
};
exports.setJevBudgetSchema = zod_1.z.object({ budgetMicroUsd: exports.jevBudgetMicroUsdSchema });
exports.raiseJevBudgetSchema = exports.setJevBudgetSchema;
//# sourceMappingURL=jev-budget.js.map